import React, { useState } from 'react';
import { toast } from 'react-toastify';
import TextField from '@mui/material/TextField';
import { formatDate } from '../../utils/dateUtils';
import CaseTicketService from './CaseTicketService';

// ตกหล่น: a found parcel that had already reached Thailand before the system
// linked it — our mistake. The supervisor picks a compensation (a weight-fee
// share) before the customer is told. ไม่ชดเชย = the normal found message, with
// no apology. The dates here are for staff only; the customer's message has none.
const SupervisorLapsePanel = ({ caseTicket, items, onDone }) => {
  const [modes, setModes] = useState({});       // itemId -> 'free' | 'percent' | 'none'
  const [percents, setPercents] = useState({}); // itemId -> string, when mode is 'percent'
  const [saving, setSaving] = useState(false);

  const lapsed = items.filter((it) => it.foundStatus === 'found' && it.lapsedAt && it.lapsePercent == null);
  if (!lapsed.length) return null;

  const percentFor = (it) => {
    const mode = modes[it.id];
    if (mode === 'free') return 100;
    if (mode === 'none') return 0;
    if (mode !== 'percent') return null;
    const n = Number(percents[it.id]);
    return percents[it.id] !== '' && Number.isFinite(n) && n > 0 && n <= 100 ? n : null;
  };
  const undecided = lapsed.filter((it) => percentFor(it) === null);

  const submit = async () => {
    if (undecided.length) { toast.error(`ยังไม่ได้เลือก ${undecided.length} รายการ`); return; }
    const summary = lapsed.map((it) => {
      const v = percentFor(it);
      return `• ${v >= 100 ? 'ฟรีค่าน้ำหนัก' : v > 0 ? `ลดค่าน้ำหนัก ${v}%` : 'ไม่ชดเชย (แจ้งเจอของตามปกติ)'}`;
    }).join('\n');
    if (!window.confirm(`ยืนยันการชดเชย?\n${summary}\n\nระบบจะร่างข้อความให้แอดมินไปแจ้งลูกค้า`)) return;
    setSaving(true);
    try {
      await CaseTicketService.setLapse(caseTicket.id, Object.fromEntries(lapsed.map((it) => [it.id, percentFor(it)])));
      toast.success('บันทึกแล้ว — ร่างข้อความให้แอดมินแล้ว');
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const pill = (on, onCls, offCls) => `text-xs px-3 py-1.5 rounded-full border font-medium ${on ? onCls : `bg-white ${offCls}`}`;

  return (
    <div className="border-2 border-red-400 bg-red-50 rounded-lg p-4 mb-4">
      <h3 className="font-bold text-red-900 mb-1">⚠️ ตกหล่น — {lapsed.length} รายการ ของถึงไทยแล้วแต่ระบบไม่ได้ผูก</h3>
      <p className="text-sm text-red-800 mb-3">
        เลือกสิ่งที่ชดเชยให้ลูกค้าก่อนแจ้ง · ถ้า <b>ไม่ชดเชย</b> ลูกค้าจะได้ข้อความเจอของตามปกติ (ไม่มีคำขอโทษ)
      </p>

      <div className="flex flex-col gap-2 mb-3">
        {lapsed.map((it) => {
          const mode = modes[it.id];
          return (
            <div key={it.id} className={`bg-white border rounded p-3 ${percentFor(it) === null ? 'border-amber-300' : 'border-red-100'}`}>
              <a href={it.url} target="_blank" rel="noreferrer" className="block truncate text-blue-700 underline text-sm">{it.url}</a>
              <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 mb-2">
                <span>{it.priceYen != null ? `${Number(it.priceYen).toLocaleString()} เยน` : '—'}</span>
                <span>{it.trackingJp && it.trackingJp !== 'ไม่มีเลข' ? it.trackingJp : 'ไม่มีเลขพัสดุ'}</span>
                {it.foundShipDate && (
                  <span className="text-red-700">
                    {it.foundShipDate.isAir ? '✈️ แอร์' : '🚢 เรือ'} {formatDate(it.foundShipDate.shipDate)}
                    {it.foundShipDate.thaiDate ? ` · ถึงไทย ${formatDate(it.foundShipDate.thaiDate)}` : ''}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => setModes((p) => ({ ...p, [it.id]: 'free' }))}
                  className={pill(mode === 'free', 'bg-emerald-600 text-white border-emerald-600', 'text-emerald-700 border-emerald-300')}>ฟรีค่าน้ำหนัก</button>
                <button type="button" onClick={() => {
                  setModes((p) => ({ ...p, [it.id]: 'percent' }));
                  if (percents[it.id] === undefined) setPercents((p) => ({ ...p, [it.id]: '50' }));
                }}
                  className={pill(mode === 'percent', 'bg-blue-600 text-white border-blue-600', 'text-blue-700 border-blue-300')}>ลดค่าน้ำหนัก %</button>
                <button type="button" onClick={() => setModes((p) => ({ ...p, [it.id]: 'none' }))}
                  className={pill(mode === 'none', 'bg-gray-600 text-white border-gray-600', 'text-gray-700 border-gray-300')}>ไม่ชดเชย</button>
                {mode === 'percent' && (
                  <TextField size="small" sx={{ width: 110, bgcolor: 'white' }} label="%" inputMode="decimal"
                    value={percents[it.id] ?? ''}
                    onChange={(e) => setPercents((p) => ({ ...p, [it.id]: e.target.value.replace(/[^\d.]/g, '') }))} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={submit} disabled={saving || undecided.length > 0}
        className="bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg font-medium">
        บันทึก & ร่างข้อความให้แอดมิน
      </button>
      {undecided.length > 0 && (
        <p className="text-xs text-amber-700 mt-1">ยังไม่ได้เลือก {undecided.length} รายการ</p>
      )}
    </div>
  );
};

export default SupervisorLapsePanel;
